<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="min-width: 800px; max-width: 900px">
      <!-- Header -->
      <q-card-section class="row items-center bg-primary text-white">
        <q-icon name="dashboard" size="sm" class="q-mr-sm" />
        <div>
          <div class="text-h6">Controller I/O Usage</div>
          <div v-if="controller" class="text-caption">{{ controller.name }}</div>
        </div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense />
      </q-card-section>

      <q-separator />

      <!-- Loading State -->
      <div v-if="loading" class="q-pa-xl text-center">
        <q-spinner color="primary" size="50px" />
        <div class="text-caption text-grey-7 q-mt-md">Loading I/O usage...</div>
      </div>

      <!-- Content -->
      <div v-else-if="controller">
        <!-- Summary Stats -->
        <q-card-section>
          <div class="row q-col-gutter-md">
            <!-- UI -->
            <div v-if="ioCapacity.UI > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-purple">
                    {{ ioUsage.UI || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.UI || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Universal Inputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('UI')"
                    color="purple"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('UI', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- UO -->
            <div v-if="ioCapacity.UO > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-indigo">
                    {{ ioUsage.UO || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.UO || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Universal Outputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('UO')"
                    color="indigo"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('UO', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- AI -->
            <div v-if="ioCapacity.AI > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-blue">
                    {{ ioUsage.AI || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.AI || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Analog Inputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('AI')"
                    color="blue"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('AI', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- AO -->
            <div v-if="ioCapacity.AO > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-green">
                    {{ ioUsage.AO || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.AO || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Analog Outputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('AO')"
                    color="green"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('AO', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- DI -->
            <div v-if="ioCapacity.DI > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-orange">
                    {{ ioUsage.DI || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.DI || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Digital Inputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('DI')"
                    color="orange"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('DI', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- DO -->
            <div v-if="ioCapacity.DO > 0" class="col-3">
              <q-card flat bordered class="text-center">
                <q-card-section>
                  <div class="text-h4 text-red">
                    {{ ioUsage.DO || 0 }}<span class="text-body2 text-grey-6">/ {{ ioCapacity.DO || 0 }}</span>
                  </div>
                  <div class="text-caption text-grey-7">Digital Outputs</div>
                  <q-linear-progress
                    :value="getUsagePercent('DO')"
                    color="red"
                    class="q-mt-sm"
                    size="8px"
                  />
                  <div class="text-caption text-grey-6 q-mt-xs">
                    {{ getUsagePercent('DO', true) }}% used
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </q-card-section>

        <q-separator />

        <!-- Assigned Equipment List -->
        <q-card-section v-if="assignedEquipment.length > 0">
          <div class="text-subtitle2 q-mb-md">Assigned Equipment</div>
          <q-list dense bordered separator>
            <q-item
              v-for="equip in assignedEquipment"
              :key="equip.id"
              clickable
              @click="navigateToEquipment(equip.id)"
            >
              <q-item-section avatar>
                <q-icon name="device_hub" color="primary" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ equip.name }}</q-item-label>
                <q-item-label caption>{{ equip.brick_class }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge color="grey-7">
                  {{ formatIoRequirements(equip.io_requirements) }}
                </q-badge>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-separator />

        <!-- I/O Point Details -->
        <q-card-section>
          <div class="text-subtitle2 q-mb-md">Connected I/O Points</div>

          <!-- Filter Tabs -->
          <q-tabs
            v-model="filterType"
            dense
            active-color="primary"
            class="q-mb-md"
          >
            <q-tab name="all" label="All" />
            <q-tab v-for="ioType in availableIoTypes" :key="ioType" :name="ioType" :label="ioType" :class="getIoTypeClass(ioType)" />
          </q-tabs>

          <!-- Points Table -->
          <q-table
            :rows="filteredPoints"
            :columns="columns"
            row-key="id"
            flat
            bordered
            dense
            :rows-per-page-options="[10, 25, 50]"
          >
            <template #body-cell-io_type="props">
              <q-td :props="props">
                <q-badge :color="getIoTypeColor(props.row.io_type)">
                  {{ props.row.io_type }}:{{ props.row.io_index }}
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-equipment="props">
              <q-td :props="props">
                <q-btn
                  flat
                  dense
                  no-caps
                  :label="props.row.equipment_name || 'Unknown'"
                  color="primary"
                  size="sm"
                  @click="navigateToEquipment(props.row.equipment_id)"
                />
              </q-td>
            </template>

            <template #body-cell-status="props">
              <q-td :props="props">
                <q-badge :color="props.row.connected ? 'positive' : 'grey'">
                  {{ props.row.connected ? 'Connected' : 'Available' }}
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-signal_type="props">
              <q-td :props="props">
                <div class="row items-center">
                  <span>{{ props.row.signal_type_display }}</span>
                  <q-btn
                    v-if="props.row.connected"
                    flat
                    dense
                    round
                    size="xs"
                    icon="edit"
                    color="grey-7"
                    class="q-ml-xs"
                    @click="editSignalType(props.row)"
                  >
                    <q-tooltip>Edit signal type</q-tooltip>
                  </q-btn>
                </div>
              </q-td>
            </template>

            <template #body-cell-end_device="props">
              <q-td :props="props">
                <div v-if="props.row.device_info">
                  <!-- Minimal display: just part number -->
                  <div v-if="props.row.device_info.part" class="text-caption">
                    P/N: {{ props.row.device_info.part.part_number }}
                  </div>
                  <div v-else class="text-caption text-grey-7">
                    No part assigned
                  </div>

                  <!-- Single button: EDIT PART -->
                  <q-btn
                    flat
                    dense
                    size="xs"
                    icon="inventory"
                    color="primary"
                    label="EDIT PART"
                    class="q-mt-xs"
                    @click="editDevicePart(props.row)"
                  />
                </div>
                <div v-else class="text-grey-7 text-caption">No device</div>
              </q-td>
            </template>
          </q-table>
        </q-card-section>
      </div>

      <!-- Empty State -->
      <q-card-section v-else class="text-center text-grey-6 q-py-xl">
        <q-icon name="warning" size="48px" class="q-mb-sm" />
        <div>No controller data available</div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right">
        <q-btn v-close-popup flat label="Close" />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Edit Signal Type Dialog -->
  <q-dialog v-model="showEditDialog" persistent>
    <q-card style="min-width: 400px">
      <q-card-section class="row items-center bg-primary text-white">
        <q-icon name="settings" size="sm" class="q-mr-sm" />
        <div class="text-h6">Edit Signal Type</div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense />
      </q-card-section>

      <q-card-section v-if="editingPoint">
        <div class="text-subtitle2 q-mb-sm">{{ editingPoint.terminal_display }}</div>
        <div class="text-caption text-grey-7 q-mb-md">{{ editingPoint.point_name }}</div>

        <q-select
          v-model="editForm.signal_type"
          :options="signalTypeOptions"
          label="Signal Type"
          outlined
          dense
          class="q-mb-md"
        />

        <q-select
          v-model="editForm.signal_subtype"
          :options="getSignalSubtypeOptions(editForm.signal_type)"
          label="Signal Subtype"
          outlined
          dense
          :disable="!editForm.signal_type"
        />

        <div class="text-caption text-grey-7 q-mt-md">
          Tip: Signal types are auto-detected based on point type. Override only if incorrect.
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Reset to Auto" color="orange" @click="resetSignalType" />
        <q-btn v-close-popup flat label="Cancel" />
        <q-btn label="Save" color="primary" @click="saveSignalType" />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Device Part Edit Dialog -->
  <q-dialog v-model="editingDevicePart">
    <q-card style="min-width: 500px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">Edit Device Part</div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense />
      </q-card-section>

      <q-card-section>
        <div class="text-body2 q-mb-md">
          Device: <strong>{{ editingDevicePartRow?.device_info?.name }}</strong>
        </div>

        <q-select
          v-model="devicePartForm.device_part"
          :options="devicePartOptionsWithAdd"
          label="Search Device Part"
          filled
          use-input
          clearable
          option-label="display"
          hint="Search by part number, manufacturer, or model"
          @filter="filterDeviceParts"
          @update:model-value="handlePartSelected"
        >
          <template #prepend><q-icon name="search" /></template>

          <template #no-option>
            <q-item><q-item-section class="text-grey">Type 2+ characters...</q-item-section></q-item>
          </template>

          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section v-if="scope.opt.isAddNew">
                <q-item-label>
                  <q-icon name="add_circle" color="primary" class="q-mr-sm" />
                  {{ scope.opt.display }}
                </q-item-label>
              </q-item-section>
              <q-item-section v-else>
                <q-item-label>{{ scope.opt.part_number }}</q-item-label>
                <q-item-label caption>{{ scope.opt.manufacturer }} - {{ scope.opt.model }}</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <q-card v-if="devicePartForm.device_part" flat bordered class="bg-blue-1 q-mt-md">
          <q-card-section class="q-pa-sm">
            <div class="row items-center justify-between q-mb-sm">
              <div class="text-subtitle2">Selected Part Details</div>
              <q-btn
                flat
                dense
                color="primary"
                icon="edit"
                label="Edit Part in Registry"
                size="sm"
                @click="openEditPartInRegistry"
              >
                <q-tooltip>Edit this part in the registry (affects all devices using this part)</q-tooltip>
              </q-btn>
            </div>
            <div class="row q-col-gutter-sm">
              <div class="col-12">
                <div class="text-caption text-grey-7">Part Number</div>
                <div>{{ devicePartForm.device_part.part_number }}</div>
              </div>
              <div class="col-6">
                <div class="text-caption text-grey-7">Manufacturer</div>
                <div>{{ devicePartForm.device_part.manufacturer }}</div>
              </div>
              <div class="col-6">
                <div class="text-caption text-grey-7">Model</div>
                <div>{{ devicePartForm.device_part.model }}</div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn v-close-popup flat label="Cancel" />
        <q-btn
v-if="devicePartForm.device_part_id" flat label="Clear Part"
          color="orange"
          @click="devicePartForm.device_part = null; devicePartForm.device_part_id = null" />
        <q-btn unelevated label="Save" color="primary" @click="saveDevicePart" />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Add New Part Dialog (Shared Component) -->
  <AddDevicePartDialog
    v-model="showAddPartDialog"
    :part-number="addPartInitialData.partNumber"
    :brick-class="addPartInitialData.brickClass"
    @created="handlePartCreatedFromIo"
  />

  <!-- Edit Device Part Dialog (Shared Component) -->
  <EditDevicePartDialog
    v-model="editPartDialogOpen"
    :part-id="editPartId"
    @updated="handlePartUpdated"
  />
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { Notify } from 'quasar';
  import { useEquipmentAssignment } from '@/composables/equipment/useEquipmentAssignment';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import AddDevicePartDialog from '@/components/sites/dialogs/AddDevicePartDialog.vue';
  import EditDevicePartDialog from '@/components/sites/dialogs/EditDevicePartDialog.vue';

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    controller: { type: Object, default: null },
    siteId: { type: Number, required: true }, // Site ID is required for API calls
  });

  const emit = defineEmits(['update:modelValue', 'navigate-to-equipment', 'edit-device']);

  // Composables
  const { getIoTypeColor, formatIoCapacity } = useEquipmentAssignment();

  // State
  const loading = ref(false);
  const filterType = ref('all');
  const ioPoints = ref([]);
  const assignedEquipment = ref([]);

  // Device Part editing
  const editingDevicePart = ref(false);
  const editingDevicePartRow = ref(null);
  const devicePartForm = ref({ device_id: null, device_part_id: null, device_part: null });
  const devicePartOptions = ref([]);
  const currentPartSearchTerm = ref('');

  // Add New Part dialog
  const showAddPartDialog = ref(false);
  const addPartInitialData = ref({
    partNumber: '',
    brickClass: '',
  });

  // Edit Part in Registry dialog (Shared Component)
  const editPartDialogOpen = ref(false);
  const editPartId = ref(null);

  // Computed
  const ioCapacity = computed(() => props.controller?.controller_template?.io_capacity || { AI: 0, AO: 0, DI: 0, DO: 0, UI: 0, UO: 0 });

  // Calculate actual usage from connected terminals
  const ioUsage = computed(() => {
    const usage = { AI: 0, AO: 0, DI: 0, DO: 0, UI: 0, UO: 0 };
    ioPoints.value.forEach((point) => {
      if (point.connected && point.io_type) {
        usage[point.io_type] = (usage[point.io_type] || 0) + 1;
      }
    });
    return usage;
  });

  // Get only I/O types that this controller actually has
  const availableIoTypes = computed(() => {
    const types = [];
    const capacity = ioCapacity.value;
    if (capacity.AI > 0) {types.push('AI');}
    if (capacity.AO > 0) {types.push('AO');}
    if (capacity.DI > 0) {types.push('DI');}
    if (capacity.DO > 0) {types.push('DO');}
    if (capacity.UI > 0) {types.push('UI');}
    if (capacity.UO > 0) {types.push('UO');}
    return types;
  });

  const filteredPoints = computed(() => {
    if (filterType.value === 'all') {return ioPoints.value;}
    return ioPoints.value.filter((p) => p.io_type === filterType.value);
  });

  const columns = [
    { name: 'terminal', label: 'I/O', field: 'terminal_display', align: 'center', sortable: true },
    { name: 'signal_type', label: 'Type', field: 'signal_type_display', align: 'left', sortable: true },
    { name: 'point_name', label: 'Point Name', field: 'point_name', align: 'left', sortable: true },
    { name: 'equipment', label: 'Equipment', field: 'equipment_name', align: 'left', sortable: true },
    { name: 'end_device', label: 'End Device', field: 'device_info', align: 'left', sortable: true },
    { name: 'status', label: 'Status', field: 'connected', align: 'center' },
  ];

  // Methods
  function getUsagePercent(ioType, asInt = false) {
    const capacity = ioCapacity.value[ioType] || 0;
    const usage = ioUsage.value[ioType] || 0;

    if (capacity === 0) {return asInt ? 0 : 0;}

    const percent = (usage / capacity) * 100;
    return asInt ? Math.round(percent) : percent / 100;
  }

  function formatIoRequirements(requirements) {
    if (!requirements) {return 'N/A';}
    const { AI = 0, AO = 0, DI = 0, DO = 0 } = requirements;
    return `${AI} AI, ${AO} AO, ${DI} DI, ${DO} DO`;
  }

  function navigateToEquipment(equipmentId) {
    emit('navigate-to-equipment', equipmentId);
    emit('update:modelValue', false);
  }

  function editDevice(deviceId) {
    emit('edit-device', deviceId);
  }

  function getIoTypeClass(ioType) {
    const classMap = {
      'AI': 'text-blue',
      'AO': 'text-green',
      'DI': 'text-orange',
      'DO': 'text-red',
      'UI': 'text-purple',
      'UO': 'text-indigo',
    };
    return classMap[ioType] || '';
  }

  // Edit device part
  function editDevicePart(row) {
    if (!row.device_info) {return;}

    editingDevicePartRow.value = row;
    devicePartForm.value = {
      device_id: row.device_info.id,
      device_part_id: row.device_info.part?.id || null,
      device_part: row.device_info.part || null,
    };
    editingDevicePart.value = true;
  }

  // Filter device parts
  const filterDeviceParts = async (val, update, abort) => {
    currentPartSearchTerm.value = val;

    if (val.length < 2) {
      abort();
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/device-parts/search?q=${val}`);
      const data = await response.json();

      update(() => {
        devicePartOptions.value = (data.data || []).map((part) => ({
          ...part,
          display: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        }));
      });
    } catch (error) {
      abort();
    }
  };

  // Computed: Device part options with "Add New"
  const devicePartOptionsWithAdd = computed(() => {
    const options = [...devicePartOptions.value];

    if (currentPartSearchTerm.value?.length >= 2 && options.length === 0) {
      options.push({
        id: '__add_new__',
        isAddNew: true,
        display: `➕ Add "${currentPartSearchTerm.value}" as new part`,
        searchTerm: currentPartSearchTerm.value,
      });
    }

    return options;
  });

  // Handle part selection in assignment dialog
  const handlePartSelected = (part) => {
    if (!part) {
      devicePartForm.value.device_part = null;
      devicePartForm.value.device_part_id = null;
      return;
    }

    // Check if user selected "Add new part" option
    if (part.isAddNew) {
      // Open dialog with pre-filled part number and brick_class
      addPartInitialData.value = {
        partNumber: part.searchTerm,
        brickClass: editingDevicePartRow.value?.device_info?.brick_class || '',
      };
      showAddPartDialog.value = true;
      // Clear selection to show dialog properly
      devicePartForm.value.device_part = null;
      devicePartForm.value.device_part_id = null;
      return;
    }

    // Normal part selection
    devicePartForm.value.device_part = part;
    devicePartForm.value.device_part_id = part.id;
  };

  // Handle part created from shared dialog
  const handlePartCreatedFromIo = (createdPart) => {
    // Add display property for the part selector
    const partWithDisplay = {
      ...createdPart,
      display: `${createdPart.part_number} - ${createdPart.manufacturer} ${createdPart.model}`,
    };

    // Auto-select the newly created part
    devicePartForm.value.device_part = partWithDisplay;
    devicePartForm.value.device_part_id = createdPart.id;
  };

  // Edit Part in Registry functions (Shared Component)
  const openEditPartInRegistry = () => {
    if (!devicePartForm.value.device_part || !devicePartForm.value.device_part.id) {
      return;
    }

    editPartId.value = devicePartForm.value.device_part.id;
    editPartDialogOpen.value = true;
  };

  const handlePartUpdated = async (updatedPart) => {
    // Update the selected part in the assignment dialog
    if (devicePartForm.value.device_part?.id === updatedPart.id) {
      devicePartForm.value.device_part = {
        ...updatedPart,
        display: `${updatedPart.part_number} - ${updatedPart.manufacturer} ${updatedPart.model}`,
      };
    }

    // Reload I/O data to reflect updated signal types from device part specs
    await loadIoUsage();
  };

  // Save device part
  async function saveDevicePart() {
    try {
      const response = await authenticatedFetch(
        `/api/v2/sites/${props.siteId}/devices/${devicePartForm.value.device_id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ device_part_id: devicePartForm.value.device_part_id }),
        },
      );

      if (!response.ok) {throw new Error('Failed');}

      Notify.create({ type: 'positive', message: 'Device part updated' });
      editingDevicePart.value = false;
      await loadIoUsage();

    } catch (error) {
      Notify.create({ type: 'negative', message: 'Failed to update device part' });
    }
  }

  async function loadIoUsage() {
    if (!props.controller) {return;}

    loading.value = true;
    try {
      // Fetch fresh controller data from API to get updated signal types

      const response = await authenticatedFetch(`/api/v2/sites/${props.siteId}/equipment/${props.controller.id}`);
      const data = await response.json();
      const freshController = data.data;


      // Get controller terminals (not regular points)
      // Backend sends camelCase: controllerTerminals, ioConnections
      const terminals = freshController.controllerTerminals || freshController.controller_terminals || [];
      const connections = freshController.ioConnections || freshController.io_connections || [];

      // Get assigned equipment
      assignedEquipment.value = freshController.assignedEquipment || freshController.assigned_equipment || [];

      // Build a map of equipment by ID for quick lookup
      const equipmentMap = {};
      assignedEquipment.value.forEach((equip) => {
        equipmentMap[equip.id] = equip;
      });

      // Build a map of connections by io_point_id
      const connectionMap = {};
      connections.forEach((conn) => {
        const ioPointId = conn.io_point_id || conn.ioPointId;
        connectionMap[ioPointId] = conn;
      });

      // Merge terminals with connection info
      ioPoints.value = terminals.map((terminal) => {
        const connection = connectionMap[terminal.id];
        const ioType = terminal.io_type || terminal.ioType;
        const ioIndex = terminal.io_index !== undefined ? terminal.io_index : terminal.ioIndex;

        // Create 1-based terminal display (UI1, UI2, etc.)
        const terminalDisplay = `${ioType}${(ioIndex || 0) + 1}`;

        // Extract connection data
        let displayName = null;
        let equipmentName = null;
        let signalTypeDisplay = 'Not configured';

        // Initialize device_info as null
        let deviceInfo = null;

        if (connection) {
          // Get the actual connected point
          const connectedPoint = connection.connectedTo || connection.connected_to;

          // Prefer device name over point name (e.g., "Outside Air Temperature Sensor" vs "Temperature")
          const device = connectedPoint?.device;
          const equipment = connectedPoint?.equipment;

          displayName = device?.name || connectedPoint?.name || null;
          equipmentName = equipment?.name || null;

          // Build device info object with fallback to devicePart registry
          if (device) {
            const devicePart = device.devicePart || device.device_part;
            deviceInfo = {
              id: device.id,
              name: device.name,
              manufacturer: device.manufacturer || devicePart?.manufacturer,
              model: device.model_number || devicePart?.model,
              serial: device.serial_number,
              part_number: devicePart?.part_number,
              part: devicePart ? {
                id: devicePart.id,
                part_number: devicePart.part_number,
                manufacturer: devicePart.manufacturer,
                model: devicePart.model,
              } : null,
            };

            // Signal type auto-assignment with priority:
            // 1. Manual override (highest priority)
            // 2. Auto from device part specifications
            // 3. Fallback to "Not configured"
            let signalType = null;
            let signalSubtype = null;
            let isManual = false;

            if (connection.signal_type_override || connection.signalTypeOverride) {
              // Manual override exists
              signalType = connection.signal_type_override || connection.signalTypeOverride;
              signalSubtype = connection.signal_subtype_override || connection.signalSubtypeOverride;
              isManual = true;
            } else if (devicePart?.specifications) {
              // Auto from device part
              signalType = devicePart.specifications.signal_type;
              signalSubtype = devicePart.specifications.signal_subtype;
              isManual = false;
            }

            if (signalType) {
              const autoIndicator = isManual ? '' : '(Auto) ';
              signalTypeDisplay = signalSubtype
                ? `${autoIndicator}${signalType} (${signalSubtype})`
                : `${autoIndicator}${signalType}`;
            }
          }
        }

        return {
          id: terminal.id,
          terminal_display: terminalDisplay,
          signal_type_display: signalTypeDisplay,
          signal_type: connection?.signal_type || connection?.signalType,
          signal_subtype: connection?.signal_subtype || connection?.signalSubtype,
          point_name: displayName, // Use device name if available (e.g., "Outside Air Temperature Sensor")
          equipment_name: equipmentName,
          device_info: deviceInfo, // Device information for display and editing
          io_type: ioType,
          io_index: ioIndex,
          connected: !!connection,
          equipment_id: connection?.connected_to_id || connection?.connectedToId || null,
          connection_id: connection?.id,
        };
      });
    } finally {
      loading.value = false;
    }
  }

  // Signal type editing
  const showEditDialog = ref(false);
  const editingPoint = ref(null);
  const editForm = ref({
    signal_type: null,
    signal_subtype: null,
  });

  const signalTypeOptions = ['Digital', 'Voltage', 'Resistance', 'Current'];

  function getSignalSubtypeOptions(signalType) {
    const options = {
      'Digital': ['Dry Contact', '24V DC'],
      'Voltage': ['0-10V', '0-5V', '2-10V'],
      'Resistance': ['PT1000', 'PT100', 'NTC', '10K'],
      'Current': ['4-20mA', '0-20mA'],
    };
    return options[signalType] || [];
  }

  function editSignalType(point) {
    editingPoint.value = point;
    editForm.value = {
      signal_type: point.signal_type || null,
      signal_subtype: point.signal_subtype || null,
    };
    showEditDialog.value = true;
  }

  async function saveSignalType() {
    if (!editingPoint.value || !editingPoint.value.connection_id) {
      return;
    }

    try {
      loading.value = true;

      const response = await authenticatedFetch(`/api/v2/controller-io-connections/${editingPoint.value.connection_id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          signal_type_override: editForm.value.signal_type,
          signal_subtype_override: editForm.value.signal_subtype,
        }),
      });

      const data = await response.json();

      showEditDialog.value = false;

      // Reload the entire controller data to get fresh signal types
      await loadIoUsage();

      if (window.$q) {
        window.$q.notify({
          type: 'positive',
          message: 'Signal type updated successfully',
          timeout: 2000,
        });
      }
    } catch (error) {
      if (window.$q && !error.isAuthError) {
        window.$q.notify({
          type: 'negative',
          message: error.message || 'Failed to update signal type',
          timeout: 3000,
        });
      }
    } finally {
      loading.value = false;
    }
  }

  async function resetSignalType() {
    editForm.value = {
      signal_type: null,
      signal_subtype: null,
    };
    await saveSignalType(); // Saving null will trigger auto-detection
  }

  // Watch for controller changes
  watch(() => props.controller, (newController) => {
    if (newController) {
      loadIoUsage();
    }
  }, { immediate: true });

  // Watch for dialog open
  watch(() => props.modelValue, (isOpen) => {
    if (isOpen && props.controller) {
      loadIoUsage();
    }
  });
</script>
